import React, { useEffect, useRef, useState } from 'react';
import searchService from '../services/searchService';
import { tintFor } from '../utils/tints';

/**
 * EventLinkPicker — LINK AN EVENT on the signal composer.
 *
 * A search-select over the public event catalogue, drawn the way the top bar's
 * events section is: a 34px thumbnail (poster, or a tinted initial), the title,
 * and the date · time on the viewer's clock. Picking one turns the input into a
 * chip with a CHANGE / UNLINK pair; the composer sends the id and the server
 * re-reads the event before it will vouch for the link.
 *
 * Value: a search row `{ id, title, meta, imageUrl, startsAtUtc, href }` | null.
 * When the composer arrives with only an id (broadcast from a listing, or an
 * edit), `hydrateId` fetches that row so the chip can draw.
 */

export function whenLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`.toUpperCase();
}

function Thumb({ item, size = 34, inverted = false }) {
  return (
    <span
      aria-hidden
      className={`flex flex-shrink-0 items-center justify-center overflow-hidden border-2 font-mono text-[13px] font-bold ${inverted ? 'border-white text-white' : 'border-black text-black'}`}
      style={{ width: size, height: size, background: item.imageUrl ? undefined : tintFor(item.id || item.title) }}
    >
      {item.imageUrl
        ? <img src={item.imageUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
        : (item.title || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function EventLinkPicker({ value, onChange, hydrateId = null }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [unlinkable, setUnlinkable] = useState(false);
  const reqId = useRef(0);
  const inputRef = useRef(null);

  // Arrived with an id but no row (a broadcast, or an edit) → fetch the row so
  // the chip has a poster and a time. A refusal means the listing can't be
  // linked any more (private, gone); say so instead of silently dropping it.
  useEffect(() => {
    if (!hydrateId || (value && value.id === hydrateId)) return undefined;
    let cancelled = false;
    setHydrating(true);
    setUnlinkable(false);
    (async () => {
      const r = await searchService.getEvent(hydrateId);
      if (cancelled) return;
      setHydrating(false);
      // `hydrated` tells the composer this is a row it already knew about, not
      // a fresh pick — so an edit doesn't re-seed the window from the event.
      if (r.success && r.data) onChange?.(r.data, { hydrated: true });
      else { setUnlinkable(true); onChange?.(null, { hydrated: true }); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateId]);

  // Debounced search, newest keystroke wins.
  useEffect(() => {
    const q = query.trim();
    if (!open) return undefined;
    const t = setTimeout(async () => {
      const id = ++reqId.current;
      setSearching(true);
      const r = await searchService.searchEvents({ q, take: 8 });
      if (id !== reqId.current) return;
      setSearching(false);
      if (r.success) {
        setResults(r.data?.items || []);
        setTotal(r.data?.total || 0);
      } else {
        setResults([]);
        setTotal(0);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query, open]);

  const pick = (item) => {
    onChange?.(item);
    setQuery('');
    setResults([]);
    setOpen(false);
    setUnlinkable(false);
    inputRef.current?.blur();
  };

  if (hydrating) {
    return (
      <div className="border-2 border-black bg-white px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">
        Finding the event…
      </div>
    );
  }

  if (value) {
    return (
      <div className="flex items-stretch border-2 border-black bg-white" data-testid="event-link-chip">
        <span className="flex items-center border-r-2 border-black">
          <Thumb item={value} size={46} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-gray-500">Linked event</span>
          <span className="truncate text-[13px] font-bold text-black">{value.title}</span>
          <span className="truncate font-mono text-[10px] uppercase tracking-[0.1em] text-gray-500">
            {whenLabel(value.startsAtUtc) || value.meta}
          </span>
        </span>
        <span className="flex flex-shrink-0 flex-col justify-center gap-1 border-l-2 border-black px-3">
          <button
            type="button"
            onClick={() => { onChange?.(null); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
            className="font-mono text-[10px] font-bold uppercase tracking-wider underline hover:no-underline"
          >
            Change
          </button>
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500 underline hover:no-underline"
          >
            Unlink
          </button>
        </span>
      </div>
    );
  }

  const q = query.trim();
  const showList = open && (results.length > 0 || searching || q);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search events by name, venue or city…"
        aria-label="Search events to link"
        className="w-full border-2 border-black bg-white px-4 py-3 font-mono text-sm text-black placeholder-gray-400 outline-none focus:ring-0"
      />
      {unlinkable && (
        <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-500">
          That event can’t be linked any more — pick another, or send without one.
        </p>
      )}
      {showList && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-y-auto border-2 border-black bg-white">
          {searching && results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-3 font-mono text-[11px] uppercase tracking-wide text-gray-400">No events match.</p>
          ) : (
            <>
              {results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                  className="flex w-full items-center gap-3 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-black hover:text-white group"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <Thumb item={item} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-[14px]">{item.title}</span>
                    <span className="truncate font-mono text-[10px] uppercase tracking-[0.1em] opacity-70">
                      {whenLabel(item.startsAtUtc) || item.meta}
                    </span>
                  </span>
                  {item.tag && (
                    <span className="flex-shrink-0 font-mono text-[9px] font-bold uppercase tracking-[0.12em] opacity-75">{item.tag}</span>
                  )}
                </button>
              ))}
              {total > results.length && (
                <p className="border-t-2 border-black px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-gray-500">
                  {total} matches · keep typing to narrow it
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
